import React, {
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { Modal, Button, Table } from "react-bootstrap";
import Select from "react-select";
import Swal from "sweetalert2";

import CashEntryServices from "../../../services/CashEntryServices";
import Pagination from "../Pagenation/Pagenation";

const roleStatuses = {
  cashmaker: ["pendingAdmin", "pendingAccounts"],
  cashchecker: ["pendingAdmin"],
  accounts: ["pendingAccounts"]
};

const statusLabels = {
  pendingAdmin: "Cash Checker",
  pendingAccounts: "Accounts"
};

const readEntries = (response) => {
  const body = response?.data?.status ? response.data : response;

  if (!Array.isArray(body?.data?.entries)) {
    throw new Error("Unexpected cash entry API response.");
  }

  return body.data.entries;
};

const getUserRole = () => {
  try {
    const user = JSON.parse(localStorage.getItem("userDetails"));
    return user?.data?.user?.role || "";
  } catch {
    return "";
  }
};

const toInitCap = (value = "") =>
  String(value)
    .trim()
    .toLowerCase()
    .replace(/\b[a-z]/g, (letter) => letter.toUpperCase());

const getAmount = (value) => {
  const amount = Number(value);
  return Number.isFinite(amount) ? amount : 0;
};

const formatAmount = (value) =>
  getAmount(value).toLocaleString("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);

  return Number.isNaN(date.getTime())
    ? "-"
    : date.toLocaleString("en-IN");
};

const CashEntryPendingModal = ({
  show,
  handleClose,
  userRole = ""
}) => {
  const role = String(userRole || getUserRole())
    .trim()
    .toLowerCase();

  const allowed = Boolean(roleStatuses[role]);
  const canReview = ["cashchecker", "accounts"].includes(role);
  const reviewStatus =
    role === "cashchecker" ? "pendingAdmin" : "pendingAccounts";

  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);
  const [processing, setProcessing] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);

  const [activeTab, setActiveTab] = useState("in");
  const [selectedStore, setSelectedStore] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const actionLock = useRef(false);
  const selectPageRef = useRef(null);

  useEffect(() => {
    if (!show) return;

    setActiveTab("in");
    setSelectedStore("");
    setSearch("");
    setPage(0);
    setSelectedIds([]);
  }, [show, role]);

  useEffect(() => {
    if (!show || !allowed) return;

    let cancelled = false;

    const loadEntries = async () => {
      setLoading(true);
      setLoadError("");
      setEntries([]);
      setSelectedIds([]);

      try {
        const responses = await Promise.all(
          roleStatuses[role].map((status) =>
            CashEntryServices.getCashEntries({ status })
          )
        );

        const unique = new Map();

        responses.flatMap(readEntries).forEach((entry) => {
          if (roleStatuses[role].includes(entry.status)) {
            unique.set(String(entry._id), entry);
          }
        });

        const result = [...unique.values()].sort(
          (a, b) =>
            new Date(b.createdAt).getTime() -
            new Date(a.createdAt).getTime()
        );

        if (!cancelled) setEntries(result);
      } catch (error) {
        if (!cancelled) {
          const message =
            error?.response?.data?.message ||
            error?.message ||
            "Unable to load pending cash entries.";

          setLoadError(message);
          Swal.fire("Error", message, "error");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadEntries();

    return () => {
      cancelled = true;
    };
  }, [show, role, allowed, refreshKey]);

  useEffect(() => {
    setPage(0);
    setSelectedIds([]);
  }, [activeTab, selectedStore, search, pageSize]);

  const storeOptions = useMemo(() => {
    const stores = new Map();

    entries.forEach((entry) => {
      const store = entry.store;

      if (store?._id) {
        stores.set(String(store._id), {
          value: String(store._id),
          label: [
            toInitCap(store.name),
            toInitCap(store.location)
          ]
            .filter(Boolean)
            .join(", ")
        });
      }
    });

    return [
      { value: "", label: "All Stores" },
      ...[...stores.values()].sort((a, b) =>
        a.label.localeCompare(b.label)
      )
    ];
  }, [entries]);

  const scopedEntries = useMemo(() => {
    const term = search.trim().toLowerCase();

    return entries.filter((entry) => {
      const storeId = String(
        entry.store?._id || entry.store || ""
      );

      if (selectedStore && storeId !== selectedStore) {
        return false;
      }

      const text = [
        entry.passbook?.name,
        entry.store?.name,
        entry.store?.location,
        entry.employee?.name,
        entry.category?.name,
        entry.vendor?.name,
        entry.vendor?.mobileNo,
        entry.voucherNo,
        entry.remark,
        entry.paymentMode,
        entry.amount,
        statusLabels[entry.status]
      ]
        .filter((value) => value !== undefined && value !== null)
        .join(" ")
        .toLowerCase();

      return !term || text.includes(term);
    });
  }, [entries, selectedStore, search]);

  const cashInEntries = scopedEntries.filter(
    (entry) => entry.entryType === "in"
  );
  const cashOutEntries = scopedEntries.filter(
    (entry) => entry.entryType === "out"
  );
  const filteredEntries =
    activeTab === "in" ? cashInEntries : cashOutEntries;

  const pendingAmount = filteredEntries.reduce(
    (total, entry) => total + getAmount(entry.amount),
    0
  );

  const totalPages = Math.max(
    1,
    Math.ceil(filteredEntries.length / pageSize)
  );
  const currentPage = Math.min(page, totalPages - 1);
  const start = currentPage * pageSize;
  const visibleEntries = filteredEntries.slice(
    start,
    start + pageSize
  );

  const selectedSet = new Set(selectedIds);

  const selectedEntries = filteredEntries.filter(
    (entry) =>
      canReview &&
      entry.status === reviewStatus &&
      selectedSet.has(String(entry._id))
  );

  const selectedAmount = selectedEntries.reduce(
    (total, entry) => total + getAmount(entry.amount),
    0
  );

  const pageIds = visibleEntries
    .filter((entry) => canReview && entry.status === reviewStatus)
    .map((entry) => String(entry._id));

  const selectedPageCount = pageIds.filter(
    (id) => selectedSet.has(id)
  ).length;

  const allPageSelected =
    pageIds.length > 0 && selectedPageCount === pageIds.length;

  useEffect(() => {
    if (selectPageRef.current) {
      selectPageRef.current.indeterminate =
        selectedPageCount > 0 && !allPageSelected;
    }
  }, [selectedPageCount, allPageSelected, canReview]);

  const toggleEntry = (entry) => {
    if (
      loading ||
      actionLock.current ||
      !canReview ||
      entry.status !== reviewStatus
    ) {
      return;
    }

    const id = String(entry._id);

    setSelectedIds((previous) =>
      previous.includes(id)
        ? previous.filter((value) => value !== id)
        : [...previous, id]
    );
  };

  const togglePage = () => {
    if (loading || actionLock.current || !canReview) return;

    setSelectedIds((previous) => {
      const next = new Set(previous);
      const allSelected = pageIds.every((id) => next.has(id));

      pageIds.forEach((id) => {
        if (allSelected) next.delete(id);
        else next.add(id);
      });

      return [...next];
    });
  };

  const handlePageChange = (nextPage) => {
    if (actionLock.current) return;

    const value = Number(nextPage);
    if (!Number.isInteger(value)) return;

    setPage(Math.min(Math.max(0, value), totalPages - 1));
  };

  const closeModal = () => {
    if (!actionLock.current) handleClose();
  };

  const reviewSelected = async (action) => {
    if (
      actionLock.current ||
      loading ||
      !canReview ||
      selectedEntries.length === 0
    ) {
      return;
    }

    const selected = [...selectedEntries];
    actionLock.current = true;
    setProcessing(true);

    try {
      const confirmation = await Swal.fire({
        title:
          action === "approve"
            ? `Approve ${selected.length} entries?`
            : `Reject ${selected.length} entries?`,
        text: `Selected amount: ${formatAmount(selectedAmount)}`,
        icon: "warning",
        input: "textarea",
        inputLabel:
          action === "reject"
            ? "Rejection remark (required)"
            : "Approval remark (optional)",
        inputPlaceholder: "Enter remark",
        showCancelButton: true,
        confirmButtonText:
          action === "approve" ? "Yes, approve" : "Yes, reject",
        cancelButtonText: "Cancel",
        confirmButtonColor:
          action === "approve" ? "#198754" : "#dc3545",
        allowOutsideClick: false,
        inputValidator: (value) =>
          action === "reject" && !String(value || "").trim()
            ? "Please enter a rejection remark."
            : undefined
      });

      if (!confirmation.isConfirmed) return;

      const remark = String(confirmation.value || "").trim();
      const successfulIds = [];
      const failures = [];

      // Existing endpoints review one entry per request.
      for (const entry of selected) {
        try {
          const body = { action, remark };

          if (role === "cashchecker") {
            await CashEntryServices.reviewByAdmin(entry._id, body);
          } else {
            await CashEntryServices.reviewByAccounts(entry._id, body);
          }

          successfulIds.push(String(entry._id));
        } catch (error) {
          failures.push({
            id: String(entry._id),
            message:
              error?.response?.data?.message ||
              error?.message ||
              "Unable to review this entry."
          });
        }
      }

      const completed = new Set(successfulIds);

      setEntries((previous) =>
        previous.filter(
          (entry) => !completed.has(String(entry._id))
        )
      );
      setSelectedIds([]);

      if (failures.length > 0) {
        await Swal.fire({
          icon: successfulIds.length ? "warning" : "error",
          title: successfulIds.length
            ? "Partially Completed"
            : "Review Failed",
          text:
            `${successfulIds.length} succeeded; ` +
            `${failures.length} failed. ` +
            `First error: ${failures[0].message}`,
          confirmButtonText: "OK",
          allowOutsideClick: false
        });
      } else {
        await Swal.fire({
          icon: "success",
          title: "Success",
          text:
            action === "approve"
              ? role === "accounts"
                ? `${successfulIds.length} entries approved and passbook balances updated.`
                : `${successfulIds.length} entries sent for Accounts approval.`
              : `${successfulIds.length} entries rejected.`,
          confirmButtonText: "OK",
          allowOutsideClick: false
        });
      }

      setRefreshKey((value) => value + 1);
    } finally {
      actionLock.current = false;
      setProcessing(false);
    }
  };

  const thStyle = {
    position: "sticky",
    top: 0,
    zIndex: 2,
    backgroundColor: "#1565c0",
    color: "#ffffff",
    whiteSpace: "nowrap",
    fontSize: "12px",
    padding: "10px"
  };

  const tdStyle = {
    fontSize: "12px",
    padding: "10px",
    verticalAlign: "middle"
  };
  const columnCount = canReview ? 13 : 12;
  const busy = loading || processing;

  return (
    <Modal
      show={show}
      enforceFocus={false}
      onHide={closeModal}
      dialogClassName="custom-modal-width"
      backdrop={processing ? "static" : true}
      keyboard={!processing}
    >
      <Modal.Header closeButton={!processing}>
        <Modal.Title style={{ fontSize: "22px", fontWeight: 600 }}>
          Pending Cash Entries
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {!allowed ? (
          <div className="text-center py-4">
            This role cannot access pending cash entries.
          </div>
        ) : (
          <>
            <div className="row g-3 align-items-center mb-4">
              <div className="col-12 col-lg-8">
                <div className="d-flex flex-wrap gap-2">
                  <span className="badge bg-success px-3 py-2">
                    Cash In: {cashInEntries.length}
                  </span>
                  <span className="badge bg-danger px-3 py-2">
                    Cash Out: {cashOutEntries.length}
                  </span>
                  <span className="badge bg-primary px-3 py-2">
                    Pending Amount: {formatAmount(pendingAmount)}
                  </span>
                </div>
              </div>

              <div className="col-12 col-lg-4 text-lg-end">
                <Button
                  variant="primary"
                  size="sm"
                  disabled={busy}
                  onClick={() => setRefreshKey((value) => value + 1)}
                >
                  <i className="fa fa-refresh me-2" aria-hidden="true" />
                  {loading ? "Loading..." : "Refresh"}
                </Button>
              </div>
            </div>

            <div className="d-flex flex-wrap gap-2 mb-3">
              <button
                type="button"
                className={`btn btn-sm ${activeTab === "in"
                  ? "btn-success"
                  : "btn-outline-success"
                  }`}
                disabled={busy}
                onClick={() => setActiveTab("in")}
                aria-pressed={activeTab === "in"}
              >
                <i className="fa fa-arrow-down me-2" aria-hidden="true" />
                Cash In ({cashInEntries.length})
              </button>

              <button
                type="button"
                className={`btn btn-sm ${activeTab === "out"
                  ? "btn-danger"
                  : "btn-outline-danger"
                  }`}
                disabled={busy}
                onClick={() => setActiveTab("out")}
                aria-pressed={activeTab === "out"}
              >
                <i className="fa fa-arrow-up me-2" aria-hidden="true" />
                Cash Out ({cashOutEntries.length})
              </button>
            </div>

            <div className="card shadow-sm border-0 mb-3">
              <div className="card-body p-2">
                <div className="row g-2 align-items-center">
                  <div className="col-12 col-md-6">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search Cash Maker / passbook / voucher / remark"
                      value={search}
                      disabled={busy}
                      onChange={(event) => setSearch(event.target.value)}
                      aria-label="Search cash entries"
                    />
                  </div>

                  <div className="col-12 col-md-4">
                    <Select
                      options={storeOptions}
                      value={
                        storeOptions.find(
                          (option) => option.value === selectedStore
                        ) || storeOptions[0]
                      }
                      onChange={(selected) =>
                        setSelectedStore(selected?.value || "")
                      }
                      placeholder="Search Store..."
                      isSearchable
                      isDisabled={busy}
                      className="custom-react-select"
                      menuPlacement="auto"
                      aria-label="Filter store"
                    />
                  </div>

                  <div className="col-12 col-md-2">
                    <select
                      className="form-control"
                      value={pageSize}
                      disabled={busy}
                      onChange={(event) =>
                        setPageSize(Number(event.target.value))
                      }
                      aria-label="Rows per page"
                    >
                      {[5, 10, 20, 50, 100].map((value) => (
                        <option key={value} value={value}>
                          {value} Rows
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {canReview && selectedEntries.length > 0 && (
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                <div>
                  <strong>Selected: {selectedEntries.length}</strong>
                  <span className="ms-3">
                    Amount: {formatAmount(selectedAmount)}
                  </span>
                </div>

                <div className="d-flex flex-wrap gap-2">
                  <Button
                    variant="success"
                    size="sm"
                    disabled={busy}
                    onClick={() => reviewSelected("approve")}
                  >
                    <i className="fa fa-check me-1" aria-hidden="true" />
                    Approve Selected ({selectedEntries.length})
                  </Button>

                  <Button
                    variant="danger"
                    size="sm"
                    disabled={busy}
                    onClick={() => reviewSelected("reject")}
                  >
                    <i className="fa fa-times me-1" aria-hidden="true" />
                    Reject Selected ({selectedEntries.length})
                  </Button>

                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={busy}
                    onClick={() => setSelectedIds([])}
                  >
                    Clear
                  </Button>
                </div>
              </div>
            )}

            {processing && (
              <div className="text-primary mb-3" role="status">
                <span
                  className="spinner-border spinner-border-sm me-2"
                  aria-hidden="true"
                />
                Review in progress. Please wait.
              </div>
            )}

            <div
              className="scrolltable"
              style={{ maxHeight: "55vh", overflow: "auto" }}
            >
              <Table
                bordered
                hover
                className="req-sticky-table"
                style={{ marginBottom: 0 }}
              >
                <thead className="sticky-table-header">
                  <tr>
                    {canReview && (
                      <th style={{ ...thStyle, textAlign: "center" }}>
                        <input
                          ref={selectPageRef}
                          type="checkbox"
                          checked={allPageSelected}
                          disabled={busy || pageIds.length === 0}
                          onChange={togglePage}
                          aria-label="Select all entries on this page"
                          title="Select this page"
                        />
                      </th>
                    )}

                    {[
                      "#",
                      "Date & Time",
                      "Store / Location",
                      "Cash Maker",
                      "Passbook",
                      "Category",
                      "Vendor Name",
                      "Amount",
                      "Payment Mode",
                      "Voucher No / Transaction ID",
                      "Remark",
                      "Pending With"
                    ].map((label) => (
                      <th key={label} style={thStyle}>
                        {label}
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={columnCount} className="text-center py-4">
                        Loading pending entries...
                      </td>
                    </tr>
                  ) : loadError ? (
                    <tr>
                      <td colSpan={columnCount} className="text-center py-4">
                        {loadError}
                        <button
                          type="button"
                          className="btn btn-primary btn-sm ms-2"
                          disabled={busy}
                          onClick={() =>
                            setRefreshKey((value) => value + 1)
                          }
                        >
                          Retry
                        </button>
                      </td>
                    </tr>
                  ) : visibleEntries.length === 0 ? (
                    <tr>
                      <td colSpan={columnCount} className="text-center py-4">
                        No pending Cash {activeTab === "in" ? "In" : "Out"}{" "}
                        entries found.
                      </td>
                    </tr>
                  ) : (
                    visibleEntries.map((entry, index) => (
                      <tr key={entry._id}>
                        {canReview && (
                          <td style={{ ...tdStyle, textAlign: "center" }}>
                            <input
                              type="checkbox"
                              checked={selectedSet.has(String(entry._id))}
                              disabled={busy || entry.status !== reviewStatus}
                              onChange={() => toggleEntry(entry)}
                              aria-label={`Select entry ${start + index + 1}`}
                            />
                          </td>
                        )}

                        <td style={tdStyle}>{start + index + 1}</td>

                        <td style={{ ...tdStyle, whiteSpace: "nowrap" }}>
                          {formatDate(entry.dateTime)}
                        </td>

                        <td style={tdStyle}>
                          <div>{toInitCap(entry.store?.name) || "-"}</div>
                          <small>{toInitCap(entry.store?.location)}</small>
                        </td>

                        <td style={tdStyle}>
                          {toInitCap(entry.employee?.name) || "-"}
                        </td>

                        <td style={tdStyle}>
                          {toInitCap(entry.passbook?.name) || "-"}
                        </td>

                        <td style={tdStyle}>
                          {entry.category?.name || "-"}
                        </td>

                        <td style={tdStyle}>
                          {entry.vendor?.name || "-"}
                        </td>

                        <td
                          className={
                            activeTab === "in"
                              ? "text-success fw-bold"
                              : "text-danger fw-bold"
                          }
                          style={{
                            ...tdStyle,
                            textAlign: "right",
                            whiteSpace: "nowrap"
                          }}
                        >
                          {formatAmount(entry.amount)}
                        </td>

                        <td style={tdStyle}>
                          {entry.paymentMode === "cash"
                            ? "Cash"
                            : entry.paymentMode === "online"
                              ? "Online"
                              : "-"}
                        </td>

                        <td style={tdStyle}>{entry.voucherNo || "-"}</td>

                        <td
                          style={{
                            ...tdStyle,
                            minWidth: "180px",
                            overflowWrap: "anywhere"
                          }}
                        >
                          {entry.remark || "-"}
                        </td>

                        <td style={tdStyle}>
                          <span
                            className={`badge ${entry.status === "pendingAdmin"
                              ? "bg-warning text-dark"
                              : "bg-primary"
                              }`}
                          >
                            {statusLabels[entry.status] || "-"}
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </div>

            {!loading && !loadError && (
              <div className="d-sm-flex text-center justify-content-between align-items-center mt-3">
                <div className="dataTables_info">
                  Showing {filteredEntries.length ? start + 1 : 0} to{" "}
                  {Math.min(start + pageSize, filteredEntries.length)} of{" "}
                  {filteredEntries.length} entries
                </div>

                <Pagination
                  activePage={currentPage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                />
              </div>
            )}
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button
          variant="danger"
          disabled={processing}
          onClick={closeModal}
        >
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default CashEntryPendingModal;